import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Check, Sparkles, SlidersHorizontal, ArrowDown } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo63EscalaAjuste() {
    const [nivelAlto, setNivelAlto] = useState('');
    const [nivelMedio, setNivelMedio] = useState('');
    const [nivelMinimo, setNivelMinimo] = useState('');
    const [regla, setRegla] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [flexibilidad, setFlexibilidad] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/escala_ajuste`, { withCredentials: true });
                if (data.data) {
                    if (data.data.nivelAlto) setNivelAlto(data.data.nivelAlto);
                    if (data.data.nivelMedio) setNivelMedio(data.data.nivelMedio);
                    if (data.data.nivelMinimo) setNivelMinimo(data.data.nivelMinimo);
                    if (data.data.regla) setRegla(data.data.regla);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'escala_ajuste', data: { nivelAlto, nivelMedio, nivelMinimo, regla } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [nivelAlto, nivelMedio, nivelMinimo, regla]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [nivelAlto, nivelMedio, nivelMinimo, regla, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (nivelAlto.trim().length > 0 && nivelMedio.trim().length > 0 && nivelMinimo.trim().length > 0 && regla.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/escala-ajuste/submit`, {
                nivel_alto: nivelAlto, nivel_medio: nivelMedio, nivel_minimo: nivelMinimo, regla
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setFlexibilidad(data.flexibilidad);
            setSubmitted(true);
            toast.success('Escala de ajuste definida');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#7A9BA8] to-[#5F8494] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><SlidersHorizontal className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="escala-ajuste-success-title">Escala de ajuste definida</h2>
                    <p className="text-[#7A9BA8] font-medium mb-4">Ahora tienes tres niveles para nunca abandonar</p>
                    {flexibilidad && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Flexibilidad: {flexibilidad}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#7A9BA8] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#7A9BA8] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="escala_ajuste" chapter={6} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-escala-ajuste">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Cap. 6</span><span className="text-xs text-[#7A8A93]">6.3</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Escala de ajuste</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#7A9BA8] to-[#5F8494] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><SlidersHorizontal className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">Define tus tres niveles</CardTitle><p className="text-xs sm:text-sm text-white/70">Alto, medio y mínimo para adaptarte sin abandonar</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-5">
                        <div className="space-y-2 p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                            <Label className="text-sm font-semibold text-[#5FAF9D]">Nivel alto: tu mejor versión de la acción</Label>
                            <p className="text-xs text-[#7A8A93]">Lo que harías en un día ideal con energía y tiempo</p>
                            <Textarea value={nivelAlto} onChange={e => setNivelAlto(e.target.value)} placeholder="Ej: Escribir durante 45 minutos con concentración plena..." className="border-[#5FAF9D]/20 bg-white rounded-xl min-h-[60px]" data-testid="input-nivel-alto" />
                        </div>
                        <div className="flex justify-center"><ArrowDown className="h-5 w-5 text-[#DCE3E8]" /></div>
                        <div className="space-y-2 p-4 bg-[#E8B27A]/5 rounded-xl border border-[#E8B27A]/20">
                            <Label className="text-sm font-semibold text-[#E8B27A]">Nivel medio: versión moderada</Label>
                            <p className="text-xs text-[#7A8A93]">Lo que puedes hacer en un día con energía normal</p>
                            <Textarea value={nivelMedio} onChange={e => setNivelMedio(e.target.value)} placeholder="Ej: Escribir 15 minutos sin preocuparme por la calidad..." className="border-[#E8B27A]/20 bg-white rounded-xl min-h-[60px]" data-testid="input-nivel-medio" />
                        </div>
                        <div className="flex justify-center"><ArrowDown className="h-5 w-5 text-[#DCE3E8]" /></div>
                        <div className="space-y-2 p-4 bg-[#3E5F6E]/5 rounded-xl border border-[#3E5F6E]/20">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Nivel mínimo: lo más fácil posible</Label>
                            <p className="text-xs text-[#7A8A93]">Algo tan simple que no puedas decir que no, incluso en tu peor día</p>
                            <Textarea value={nivelMinimo} onChange={e => setNivelMinimo(e.target.value)} placeholder="Ej: Abrir el documento y leer lo último que escribí..." className="border-[#3E5F6E]/20 bg-white rounded-xl min-h-[60px]" data-testid="input-nivel-minimo" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Tu regla de ajuste</Label>
                            <p className="text-xs text-[#7A8A93]">¿Cómo decidirás qué nivel usar cada día?</p>
                            <Textarea value={regla} onChange={e => setRegla(e.target.value)} placeholder='Ej: Si estoy con energía y tiempo, nivel alto. Si fue un día difícil, nivel mínimo. Nunca paro del todo.' className="border-[#DCE3E8] rounded-xl min-h-[70px]" data-testid="input-regla" />
                        </div>
                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-escala-ajuste">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Definir escala</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
